import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { dataService } from '../../services/dataService';
import { razorpayService } from '../../services/razorpayService';
import { formatAmountToPaise } from '../../utils/razorpayUtils';
import { CheckCircle, XCircle, CreditCard, Loader2, Star, Play, Clock, ArrowLeft } from 'lucide-react';

interface SubscriptionPlan {
  id: string;
  name: string;
  displayName: string;
  description: string;
  pricing: {
    monthly: number;
    yearly: number;
    currency: string;
  };
  features: {
    maxUsers: number;
    maxOrders: number;
    maxMenuItems: number;
    maxStorage: number;
    inventoryManagement: boolean;
    paymentProcessing: boolean;
    analytics: boolean;
    multiLocation: boolean;
    api: boolean;
    whiteLabel: boolean;
    prioritySupport: boolean;
    customReports: boolean;
    aiChatbot: boolean;
  };
  trial: {
    enabled: boolean;
    duration: number;
    features: string[];
  };
  isActive: boolean;
  isPopular: boolean;
  sortOrder: number;
  createdAt: any;
  updatedAt: any;
}

const featureLabels: Record<string, string> = {
  maxUsers: 'Max Users',
  maxOrders: 'Max Orders/mo',
  maxMenuItems: 'Max Menu Items',
  maxStorage: 'Storage (MB)',
  inventoryManagement: 'Inventory',
  paymentProcessing: 'Payments',
  analytics: 'Analytics',
  multiLocation: 'Multi-Location',
  api: 'API Access',
  whiteLabel: 'White Label',
  prioritySupport: 'Priority Support',
  customReports: 'Custom Reports',
  aiChatbot: 'AI Chatbot',
};

const Subscription: React.FC = () => {
  const { tenantData, refreshTenantData, isMobileBrowser } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [startingTrial, setStartingTrial] = useState(false);
  const [trialPlanId, setTrialPlanId] = useState<string | null>(null);
  const [fixingTrial, setFixingTrial] = useState(false);

  const currentPlanId = tenantData?.subscription?.planId;
  const hasActiveTrial = tenantData?.subscription?.status === 'trial';

  useEffect(() => {
    const fetchPlans = async () => {
      setLoading(true);
      try {
        // Get all plans
        const allPlans = await dataService.getAllPlans();
        setPlans(allPlans.filter((p: SubscriptionPlan) => p.isActive));
      } catch (error) {
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handlePurchase = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPlan(null);
    setProcessingPayment(false);
  };

  const handlePayment = async () => {
    if (!selectedPlan || !tenantData) return;

    setProcessingPayment(true);
    try {
      const amount = billingCycle === 'monthly' 
        ? selectedPlan.pricing.monthly 
        : selectedPlan.pricing.yearly;

      const amountInPaise = formatAmountToPaise(amount);
      const description = `${selectedPlan.displayName} - ${billingCycle} subscription`;

      const result = await razorpayService.processPayment({
        amount: amountInPaise,
        currency: selectedPlan.pricing.currency || 'INR',
        customerName: tenantData.name || tenantData.businessName || 'Customer',
        customerEmail: tenantData.email || '',
        customerContact: tenantData.phone || '',
        description,
        tenantId: tenantData.id,
        planId: selectedPlan.id,
      });

      if (!result.success) {
        alert(`Payment failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment processing failed. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleStartTrial = async (plan: SubscriptionPlan) => {
    if (!tenantData?.id) {
      console.error('❌ No tenant ID available for trial start');
      const errorMessage = isMobileBrowser 
        ? 'No tenant ID available. This is a known issue on mobile browsers. Please try refreshing the page or contact support if the issue persists.'
        : 'No tenant ID available. Please try refreshing the page.';
      alert(errorMessage);
      return;
    }

    console.log('🚀 Starting trial for plan:', plan.id, 'Tenant ID:', tenantData.id, 'Mobile Browser:', isMobileBrowser);
    setStartingTrial(true);
    setTrialPlanId(plan.id);
    
    try {
      // Start trial using the data service
      console.log('📞 Calling dataService.startTrial...');
      await dataService.startTrial(tenantData.id, plan.id);
      console.log('✅ Trial started successfully');
      
      // Refresh tenant data to get updated subscription info
      console.log('🔄 Refreshing tenant data...');
      await refreshTenantData();
      console.log('✅ Tenant data refreshed');
      
      // Show success message with mobile-specific guidance
      const successMessage = isMobileBrowser 
        ? `Trial started successfully! You now have access to ${plan.displayName} for ${plan.trial.duration} days. If you don't see the changes immediately, please refresh the page.`
        : `Trial started successfully! You now have access to ${plan.displayName} for ${plan.trial.duration} days.`;
      alert(successMessage);
    } catch (error: any) {
      console.error('❌ Error starting trial:', error);
      const errorMessage = isMobileBrowser 
        ? `Failed to start trial: ${error.message || 'Please try again or contact support.'} (Mobile browser detected)`
        : `Failed to start trial: ${error.message || 'Please try again or contact support.'}`;
      alert(errorMessage);
    } finally {
      setStartingTrial(false);
      setTrialPlanId(null);
    }
  };

  const handleFixTrial = async () => {
    if (!tenantData?.id) return;

    setFixingTrial(true);
    
    try {
      await dataService.fixTrialEndDate(tenantData.id);
      await refreshTenantData();
      alert('Trial end date has been fixed!');
    } catch (error) {
      console.error('Error fixing trial:', error);
      alert('Failed to fix trial. Please try again or contact support.');
    } finally {
      setFixingTrial(false);
    }
  };

  const canStartTrial = (plan: SubscriptionPlan) => {
    // Check if plan has trial enabled
    if (!plan.trial.enabled) return false;
    
    // Check if user already has a subscription
    if (tenantData?.subscription) return false;
    
    // Check if user is already in trial for this plan
    if (tenantData?.subscription?.planId === plan.id && tenantData?.subscription?.status === 'trial') return false;
    
    return true;
  };

  const getTrialDaysRemaining = () => {
    if (tenantData?.subscription?.status !== 'trial' || !tenantData?.subscription?.endDate) return null;
    
    let endDate: Date;
    
    // Handle both Firestore Timestamp and regular Date objects
    if (tenantData.subscription.endDate.seconds) {
      // Firestore Timestamp
      endDate = new Date(tenantData.subscription.endDate.seconds * 1000);
    } else if (tenantData.subscription.endDate.toDate) {
      // Firestore Timestamp with toDate method
      endDate = tenantData.subscription.endDate.toDate();
    } else {
      // Regular Date object
      endDate = new Date(tenantData.subscription.endDate);
    }
    
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  };

  // Get all feature keys for the table
  const featureKeys = [
    'maxUsers', 'maxOrders', 'maxMenuItems', 'maxStorage',
    'inventoryManagement', 'paymentProcessing', 'analytics', 'multiLocation',
    'api', 'whiteLabel', 'prioritySupport', 'customReports', 'aiChatbot'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
        <div className="p-4 sm:p-6 max-w-5xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500 dark:text-primary-400" />
            <span className="ml-2 text-gray-600 dark:text-gray-300">Loading plans...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      {/* Mobile Browser Warning */}
      {isMobileBrowser && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center">
                <span className="text-yellow-800 text-xs font-bold">!</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Mobile Browser Detected
              </h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                You're using a mobile browser. If you experience issues with plan selection or "no tenant found" errors, 
                try refreshing the page or using a desktop browser for the best experience.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header with Back Button for Active Trial */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-gray-900 dark:text-white">Subscription & Plans</h1>
          <p className="text-gray-600 dark:text-gray-300">Manage your subscription and explore available plans.</p>
        </div>
        {hasActiveTrial && (
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
        )}
      </div>

      {/* Current Plan */}
      <div className="mb-8 p-4 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg sm:text-xl font-semibold mb-2 text-gray-900 dark:text-white">Current Plan</h2>
        {tenantData?.subscription ? (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-1">
              <span className="text-base sm:text-lg font-bold text-primary-700 dark:text-primary-400">{tenantData.subscription.planName}</span>
              <span className={`px-2 py-1 rounded text-xs font-medium w-fit flex items-center gap-1 ${
                tenantData.subscription.status === 'active' 
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' 
                  : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
              }`}>
                {tenantData.subscription.status === 'trial' && <Clock className="w-3 h-3" />}
                {tenantData.subscription.status === 'active' ? 'Active' : 'Trial'}
              </span>
            </div>
            <div className="text-gray-500 dark:text-gray-400 text-sm mb-1">
              {tenantData.subscription.status === 'trial' ? 'Trial Period' : `Billing: ${tenantData.subscription.billingCycle || 'N/A'}`}
            </div>
            <div className="text-gray-500 dark:text-gray-400 text-sm mb-2">
              Valid till: {tenantData.subscription.endDate ? (() => {
                let endDate: Date;
                if (tenantData.subscription.endDate.seconds) {
                  endDate = new Date(tenantData.subscription.endDate.seconds * 1000);
                } else if (tenantData.subscription.endDate.toDate) {
                  endDate = tenantData.subscription.endDate.toDate();
                } else {
                  endDate = new Date(tenantData.subscription.endDate);
                }
                return endDate.toLocaleDateString();
              })() : 'N/A'}
            </div>
            {tenantData.subscription.status === 'trial' && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                  <span className="text-yellow-800 dark:text-yellow-200 font-medium">
                    {getTrialDaysRemaining()} days remaining in trial
                  </span>
                </div>
                <p className="text-yellow-700 dark:text-yellow-300 text-xs mt-1">
                  Upgrade to a paid plan before your trial expires to continue using all features.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-gray-500 dark:text-gray-400">No active subscription. Start a free trial or purchase a plan below.</div>
        )}
      </div>

      {/* Mobile: Plan Cards */}
      <div className="block lg:hidden space-y-4">
        {plans.map(plan => (
          <div key={plan.id} className={`bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4 ${plan.isPopular ? 'ring-2 ring-primary-500' : ''}`}>
            {/* Plan Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">{plan.displayName}</h3>
                  {plan.isPopular && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 rounded text-xs font-medium">
                      <Star className="w-3 h-3" />
                      Popular
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{plan.description}</p>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  ₹{plan.pricing.monthly}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">/month</span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  or ₹{plan.pricing.yearly}/year
                </div>
                {plan.trial.enabled && (
                  <div className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                    {plan.trial.duration}-day free trial available
                  </div>
                )}
              </div>
            </div>

            {/* Key Features */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Key Features:</h4>
              <div className="grid grid-cols-1 gap-2">
                {featureKeys.slice(0, 6).map(key => (
                  <div key={key} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{featureLabels[key]}</span>
                    <div className="flex items-center">
                      {typeof (plan.features as any)[key] === 'boolean' ? (
                        (plan.features as any)[key] ? <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400" /> : <XCircle className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                      ) : (
                        <span className="text-gray-900 dark:text-white font-medium">{(plan.features as any)[key] === -1 ? 'Unlimited' : (plan.features as any)[key]}</span>
                      )}
                    </div>
                  </div>
                ))}
                <div className="text-xs text-gray-500 dark:text-gray-400 text-center pt-1">
                  +{featureKeys.length - 6} more features
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className="pt-2">
              {currentPlanId === plan.id ? (
                <span className="inline-block w-full px-4 py-2 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 text-sm font-semibold text-center">
                  {tenantData?.subscription?.status === 'trial' ? 'Trial Active' : 'Current Plan'}
                </span>
              ) : canStartTrial(plan) ? (
                <button
                  className="w-full btn btn-secondary px-4 py-2 text-sm font-semibold rounded flex items-center justify-center gap-2"
                  onClick={() => handleStartTrial(plan)}
                  disabled={startingTrial && trialPlanId === plan.id}
                >
                  {startingTrial && trialPlanId === plan.id ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Starting Trial...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      Start {plan.trial.duration}-Day Trial
                    </>
                  )}
                </button>
              ) : (
                <button
                  className="w-full btn btn-primary px-4 py-2 text-sm font-semibold rounded"
                  onClick={() => handlePurchase(plan)}
                >
                  {tenantData?.subscription ? 'Upgrade' : 'Purchase'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: Plans Comparison Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700">
              <th className="p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Feature</th>
              {plans.map(plan => (
                <th key={plan.id} className="p-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                  <div className="flex items-center justify-center gap-2">
                    <span>{plan.displayName}</span>
                    {plan.isPopular && <Star className="w-4 h-4 text-primary-500 dark:text-primary-400" />}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-normal">
                    ₹{plan.pricing.monthly}/mo or ₹{plan.pricing.yearly}/year
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {featureKeys.map(key => (
              <tr key={key} className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="p-3 text-sm text-gray-700 dark:text-gray-300 font-medium">{featureLabels[key]}</td>
                {plans.map(plan => (
                  <td key={plan.id} className="p-3 text-center">
                    {typeof (plan.features as any)[key] === 'boolean' ? (
                      (plan.features as any)[key] ? <CheckCircle className="inline w-5 h-5 text-green-500 dark:text-green-400" /> : <XCircle className="inline w-5 h-5 text-gray-300 dark:text-gray-600" />
                    ) : (
                      <span className="text-gray-900 dark:text-white font-semibold">{(plan.features as any)[key] === -1 ? 'Unlimited' : (plan.features as any)[key]}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
              <td className="p-3"></td>
              {plans.map(plan => (
                <td key={plan.id} className="p-3 text-center">
                  {currentPlanId === plan.id ? (
                    <span className="inline-block px-3 py-1 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 text-xs font-semibold">
                      {tenantData?.subscription?.status === 'trial' ? 'Trial Active' : 'Current Plan'}
                    </span>
                  ) : canStartTrial(plan) ? (
                    <button
                      className="btn btn-secondary px-4 py-2 text-sm font-semibold rounded flex items-center justify-center gap-2 mx-auto"
                      onClick={() => handleStartTrial(plan)}
                      disabled={startingTrial && trialPlanId === plan.id}
                    >
                      {startingTrial && trialPlanId === plan.id ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      Start Trial
                    </>
                  )}
                    </button>
                  ) : (
                    <button
                      className="btn btn-primary px-4 py-2 text-sm font-semibold rounded"
                      onClick={() => handlePurchase(plan)}
                    >
                      {tenantData?.subscription ? 'Upgrade' : 'Purchase'}
                    </button>
                  )}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Purchase/Upgrade Modal */}
      {showModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-4 sm:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg sm:text-xl font-bold mb-2 text-gray-900 dark:text-white">{tenantData?.subscription ? 'Upgrade Plan' : 'Purchase Plan'}</h3>
            <p className="mb-4 text-sm sm:text-base text-gray-700 dark:text-gray-300">You selected <span className="font-semibold text-primary-700 dark:text-primary-400">{selectedPlan.displayName}</span></p>
            
            {/* Billing Cycle Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Billing Cycle</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  className={`px-3 py-3 rounded border text-sm font-medium transition-colors ${
                    billingCycle === 'monthly' 
                      ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' 
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  onClick={() => setBillingCycle('monthly')}
                >
                  <div className="font-semibold">Monthly</div>
                  <div className="text-xs opacity-75">₹{selectedPlan.pricing.monthly}/mo</div>
                </button>
                <button
                  className={`px-3 py-3 rounded border text-sm font-medium transition-colors ${
                    billingCycle === 'yearly' 
                      ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' 
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  onClick={() => setBillingCycle('yearly')}
                >
                  <div className="font-semibold">Yearly</div>
                  <div className="text-xs opacity-75">₹{selectedPlan.pricing.yearly}/year</div>
                </button>
              </div>
            </div>

            {/* Plan Features */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Plan Features:</h4>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {Object.entries(selectedPlan.features).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2 text-sm">
                    {typeof value === 'boolean' ? (
                      value ? <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                    ) : (
                      <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0" />
                    )}
                    <span className="text-gray-700 dark:text-gray-300">{featureLabels[key] || key}: {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value === -1 ? 'Unlimited' : value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Button */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                className="flex-1 btn btn-primary flex items-center justify-center gap-2 py-3"
                onClick={handlePayment}
                disabled={processingPayment}
              >
                {processingPayment ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4" />
                    Pay ₹{billingCycle === 'monthly' ? selectedPlan.pricing.monthly : selectedPlan.pricing.yearly}
                  </>
                )}
              </button>
              <button
                className="btn btn-secondary py-3"
                onClick={closeModal}
                disabled={processingPayment}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Subscription; 